<script lang="js" setup>
import { ref, onMounted, computed } from "vue";
import { fetchGetApi, fetchPostApi } from "@/util/api";
import { useWorkApprovalStore } from '@/store/workApproval';
import Swal from 'sweetalert2';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';

const allData = ref([]);
const filterQuery = ref("");
const currentPage = ref(1);
const itemsPerPage = ref(10);
const toast = useToast();
const showModal = ref(false);
const selectedLeaveType = ref(null);

// Form data for create/edit
const formData = ref({
    name: '',
    description: '',
    status: 'Active'
});

const isEditing = ref(false);

// Computed property for filtered and paginated data
const filteredData = computed(() => {
    let filtered = [...allData.value];

    if (filterQuery.value) {
        filtered = filtered.filter(item =>
            item.name.toLowerCase().includes(filterQuery.value.toLowerCase()) ||
            item.description.toLowerCase().includes(filterQuery.value.toLowerCase()) ||
            item.status.toLowerCase().includes(filterQuery.value.toLowerCase())
        );
    }

    return filtered;
});

const paginatedData = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredData.value.slice(start, end);
});

const totalPages = computed(() =>
    Math.ceil(filteredData.value.length / itemsPerPage.value)
);

// CRUD Operations
const loadData = async () => {
    try {
        const response = await fetchGetApi('api/leaving/type/get/all');
        console.log('API Response:', response);
        if (response && response.data) {
            // Handle the response structure with data array
            allData.value = response.data;
        } else {
            allData.value = [];
            toast.error('No data received from server');
        }
    } catch (error) {
        toast.error('Failed to fetch leave types');
        console.error('Error loading data:', error);
        allData.value = [];
    }
};

const handleCreate = async () => {
    try {
        await fetchPostApi('api/leaving/type/create', formData.value);
        toast.success('Leave type created successfully');
        await loadData();
        showModal.value = false;
        resetForm();
    } catch (error) {
        toast.error('Failed to create leave type');
    }
};

const handleEdit = async () => {
    try {
        await fetchPostApi(`api/leaving/type/edit/${selectedLeaveType.value._id}`, formData.value);
        toast.success('Leave type updated successfully');
        await loadData();
        showModal.value = false;
        resetForm();
    } catch (error) {
        toast.error('Failed to update leave type');
    }
};

const handleDelete = async (id) => {
    try {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            await fetchPostApi(`api/leaving/type/delete/${id}`);
            toast.success('Leave type deleted successfully');
            await loadData();
        }
    } catch (error) {
        toast.error('Failed to delete leave type');
    }
};

const handleStatusToggle = async (id, currentStatus) => {
    try {
        await fetchPostApi(`api/leaving/type/status/${id}`);
        toast.success('Status updated successfully');
        await loadData();
    } catch (error) {
        toast.error('Failed to update status');
    }
};

const openCreateModal = () => {
    isEditing.value = false;
    resetForm();
    showModal.value = true;
};

const openEditModal = (item) => {
    isEditing.value = true;
    selectedLeaveType.value = item;
    formData.value = {
        name: item.name,
        description: item.description,
        status: item.status
    };
    showModal.value = true;
};

const resetForm = () => {
    formData.value = {
        name: '',
        description: '',
        status: 'Active'
    };
    selectedLeaveType.value = null;
};

onMounted(async () => {
    await loadData();
});
</script>

<template>
    <Breadcrumbs main="Apps" title="Siddar Types" />
    <div class="container-fluid">
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        <div class="d-flex justify-content-between align-items-center">
                            <h5>Siddar Types</h5>
                            <button class="btn btn-primary" @click="openCreateModal">
                                <i class="fas fa-plus"></i> Add Siddar Type
                            </button>
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="mb-3">
                            <input type="text" class="form-control w-25" 
                                placeholder="Search..." v-model="filterQuery">
                        </div>
                        <div class="table-responsive">
                            <table class="table table-bordered table-hover">
                                <thead>
                                    <tr>
                                        <th>Reason</th>
                                        <!-- <th>Description</th> -->
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in paginatedData" :key="item._id">
                                        <td>{{ item.name }}</td>
                                        <!-- <td>{{ item.description }}</td> -->
                                        <td>
                                            <span :class="`badge bg-${item.status === 'Active' ? 'success' : 'danger'}`">
                                                {{ item.status }}
                                            </span>
                                        </td>
                                        <td>
                                            <button class="btn btn-info btn-sm me-2" @click="openEditModal(item)">
                                                <i class="fas fa-edit"></i>
                                            </button>
                                            <button class="btn btn-danger btn-sm me-2" @click="handleDelete(item._id)">
                                                <i class="fas fa-trash"></i>
                                            </button>
                                            <button class="btn btn-warning btn-sm" 
                                                @click="handleStatusToggle(item._id, item.status)">
                                                <i class="fas fa-sync"></i>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <!-- Pagination -->
                        <div class="d-flex justify-content-between align-items-center mt-3">
                            <div>
                                Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} to
                                {{ Math.min(currentPage * itemsPerPage, filteredData.length) }} of
                                {{ filteredData.length }} entries
                            </div>
                            <ul class="pagination mb-0">
                                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                    <a class="page-link" @click="currentPage--">Previous</a>
                                </li>
                                <li class="page-item" v-for="page in totalPages" :key="page"
                                    :class="{ active: page === currentPage }">
                                    <a class="page-link" @click="currentPage = page">{{ page }}</a>
                                </li>
                                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                    <a class="page-link" @click="currentPage++">Next</a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Create/Edit Modal -->
    <div class="modal" tabindex="-1" :class="{ 'd-block': showModal }">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">{{ isEditing ? 'Edit' : 'Create' }} Leave Type</h5>
                    <button type="button" class="btn-close" @click="showModal = false"></button>
                </div>
                <div class="modal-body">
                    <form @submit.prevent="isEditing ? handleEdit() : handleCreate()">
                        <div class="mb-3">
                            <label class="form-label">Reason</label>
                            <input type="text" class="form-control" v-model="formData.name" required>
                        </div>
                        <div class="mb-3">
                            <label class="form-label">Status</label>
                            <select class="form-select" v-model="formData.status">
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        </div>
                        <div class="text-end">
                            <button type="button" class="btn btn-secondary me-2" @click="showModal = false">
                                Cancel
                            </button>
                            <button type="submit" class="btn btn-primary">
                                {{ isEditing ? 'Update' : 'Create' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
    <!-- Modal Backdrop -->
    <div class="modal-backdrop fade show" v-if="showModal"></div>
</template>

<style scoped>
.modal {
    background-color: rgba(0, 0, 0, 0.5);
}
</style>
